import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { listMsActivations, msActivationAction, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Cloud, Loader2, ClipboardCheck, FileText, Play, PauseCircle, XCircle, RefreshCw } from "lucide-react";

const LABELS = {
  NOT_REQUESTED: "Non demandée", REQUESTED: "Demande transmise", UNDER_REVIEW: "En analyse",
  QUOTE_REQUIRED: "Soumission requise", AWAITING_CUSTOMER_APPROVAL: "Attente client",
  PAYMENT_REQUIRED: "Paiement requis", APPROVED: "Approuvée", ACTIVE: "Active",
  SUSPENDED: "Suspendue", REJECTED: "Refusée",
};

export default function MsActivationsAdminPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quoteFor, setQuoteFor] = useState(null);
  const [quote, setQuote] = useState({ setup_fee: 0, monthly_fee: 0, support_hourly_rate: 0, billing_type: "ONE_TIME", customer_message: "" });

  const load = async () => {
    setLoading(true);
    try { setRows(await listMsActivations()); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action, body) => {
    try {
      await msActivationAction(id, action, body);
      toast.success("Action effectuée.");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendQuote = async () => {
    await act(quoteFor.id, "quote", {
      setup_fee: Number(quote.setup_fee) || 0, monthly_fee: Number(quote.monthly_fee) || 0,
      support_hourly_rate: Number(quote.support_hourly_rate) || 0, billing_type: quote.billing_type,
      customer_message: quote.customer_message,
    });
    setQuoteFor(null);
  };

  return (
    <div className="space-y-6" data-testid="ms-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold text-white flex items-center gap-2.5">
            <Cloud className="w-7 h-7 text-[#EAB308]" /> Activations Microsoft Cloud Connect
          </h1>
          <p className="mt-1 text-white/55">Gestion des demandes d'activation : soumission, approbation, activation.</p>
        </div>
        <Button data-testid="ms-admin-refresh" onClick={load} className="h-9 bg-white/10 border border-white/15 text-white text-xs gap-1.5">
          <RefreshCw className="w-3.5 h-3.5" /> Actualiser
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#EAB308] animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-10 text-center text-white/50" data-testid="ms-admin-empty">
          Aucune demande d'activation pour le moment.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} data-testid={`ms-admin-row-${r.id}`} className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-white">{r.company_name || "Entreprise"}</span>
                    <span className="rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[11px] text-[#EAB308]">{LABELS[r.status] || r.status}</span>
                  </div>
                  <div className="text-xs text-white/45 mt-1">
                    Demandeur : {r.requester_name} · Modules : {(r.requested_modules || []).join(", ")} · Services : {(r.requested_services || []).join(", ")}
                  </div>
                  {r.ticket_ref && <div className="text-[11px] text-white/35 mt-0.5 font-mono">Billet : {r.ticket_ref}</div>}
                  {(r.setup_fee || r.monthly_fee) ? (
                    <div className="text-[11px] text-white/50 mt-1">Frais : {(r.setup_fee || 0).toFixed(2)}$ activation · {(r.monthly_fee || 0).toFixed(2)}$/mois</div>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {r.status === "REQUESTED" && (
                    <Button data-testid={`ms-take-${r.id}`} onClick={() => act(r.id, "take")} className="h-8 bg-white/10 border border-white/15 text-white text-xs gap-1"><ClipboardCheck className="w-3.5 h-3.5" /> Prendre en charge</Button>
                  )}
                  {["REQUESTED", "UNDER_REVIEW", "QUOTE_REQUIRED", "AWAITING_CUSTOMER_APPROVAL"].includes(r.status) && (
                    <Button data-testid={`ms-quote-${r.id}`} onClick={() => { setQuoteFor(r); setQuote({ setup_fee: r.setup_fee || 0, monthly_fee: r.monthly_fee || 0, support_hourly_rate: r.support_hourly_rate || 0, billing_type: r.billing_type || "ONE_TIME", customer_message: r.customer_message || "" }); }} className="h-8 bg-[#EAB308] text-black text-xs font-semibold gap-1"><FileText className="w-3.5 h-3.5" /> Soumission</Button>
                  )}
                  {["APPROVED", "SUSPENDED"].includes(r.status) && (
                    <Button data-testid={`ms-activate-${r.id}`} onClick={() => act(r.id, "activate")} className="h-8 bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-xs gap-1"><Play className="w-3.5 h-3.5" /> Activer</Button>
                  )}
                  {r.status === "ACTIVE" && (
                    <Button data-testid={`ms-suspend-${r.id}`} onClick={() => act(r.id, "suspend")} className="h-8 bg-white/10 border border-white/15 text-white/70 text-xs gap-1"><PauseCircle className="w-3.5 h-3.5" /> Suspendre</Button>
                  )}
                  {!["ACTIVE", "REJECTED"].includes(r.status) && (
                    <Button data-testid={`ms-reject-${r.id}`} onClick={() => act(r.id, "reject")} className="h-8 bg-transparent border border-[#dc143c]/40 text-[#ff8fa3] text-xs gap-1"><XCircle className="w-3.5 h-3.5" /> Refuser</Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!quoteFor} onOpenChange={(o) => !o && setQuoteFor(null)}>
        <DialogContent className="bg-[#0a0a0a] border-white/15 text-white" data-testid="ms-quote-dialog">
          <DialogHeader><DialogTitle>Soumission — {quoteFor?.company_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-white/70 text-xs">Frais activation ($)</Label><Input data-testid="ms-quote-setup" type="number" value={quote.setup_fee} onChange={(e) => setQuote((q) => ({ ...q, setup_fee: e.target.value }))} className="bg-black/40 border-white/15 text-white h-10" /></div>
              <div><Label className="text-white/70 text-xs">Frais mensuels ($)</Label><Input data-testid="ms-quote-monthly" type="number" value={quote.monthly_fee} onChange={(e) => setQuote((q) => ({ ...q, monthly_fee: e.target.value }))} className="bg-black/40 border-white/15 text-white h-10" /></div>
              <div><Label className="text-white/70 text-xs">Taux soutien/h ($)</Label><Input type="number" value={quote.support_hourly_rate} onChange={(e) => setQuote((q) => ({ ...q, support_hourly_rate: e.target.value }))} className="bg-black/40 border-white/15 text-white h-10" /></div>
            </div>
            <div><Label className="text-white/70 text-xs">Message au client</Label><Input value={quote.customer_message} onChange={(e) => setQuote((q) => ({ ...q, customer_message: e.target.value }))} className="bg-black/40 border-white/15 text-white h-10" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="ms-quote-send" onClick={sendQuote} className="bg-[#EAB308] text-black font-semibold">Envoyer au client</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
